import React, { useCallback, useEffect } from 'react';
import Main from './components/Main/Main';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './styles/css/App.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import useLoadFonts from './hooks/useLoadFonts';
import useLocalStorage from './hooks/useLocalStorage';
import { ConsumtionModalTemplate } from './redux/store/IStore';
import {
  getActiveQuota,
  getUserQuota,
  transformConsumedAndLimitToReadable,
  transformLimitToReadable,
  translateChunkKey,
} from './api/UserService';
import useInterval from './hooks/useInterval';
import Modal from './components/Home/Modal';
import useKeycloak from './hooks/useKeycloak';
import './app.css';
import { useAppDispatch, useAppSelector } from './redux/store';
import {
  setActiveQuota,
  setConsumptionModal,
  updateBoosts,
  updateReplacements,
} from './redux/features/app/app';
import { getReplacementSets, LocalReplacementSets } from './api/ReplacementsService';
import { getPhrases, getPhraseSetById, getSetPhrases, LocalBoostSets } from './api/PhraseService';
import ReplacementsModal from './components/Home/ReplacementsModal';
import BoostModal from './components/Home/BoostModal';
import { useSnackbar } from 'notistack';

const priorities = {
  PERSONAL: 0,
  SHARED: 1,
  GLOBAL: 2,
};

const App: React.FC = () => {
  useLoadFonts();
  useLocalStorage();

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { visible, template } = useAppSelector((state) => state.app.consumptionModal);

  const activeQuota = useAppSelector((state) => state.app.activeQuota);

  const { isAuthenticated } = useKeycloak();

  const hideConsumptionModal = () =>
    dispatch(
      setConsumptionModal({
        visible: false,
        template: undefined,
      })
    );

  const fetchQuota = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const quota = await getUserQuota();
        for (const key in quota.data) {
          quota.data[key].processedMs = quota.data[key].processedMs / 1000
          quota.data[key].limitMs = quota.data[key].limitMs / 1000
          quota.data[key].warnMs = quota.data[key].warnMs / 1000
        }
        
        dispatch(setActiveQuota(getActiveQuota(quota.data)));
      } catch (error) {
        console.log(error);
      }
    }
  }, [isAuthenticated]);

  const fetchReplacementSets = async () => {
    const storageReplacements = localStorage.getItem('replacement_sets');
    const storageReplacementsParsed = storageReplacements
      ? (JSON.parse(storageReplacements) as LocalReplacementSets)
      : null;

    try {
      const replacementsResponse = await getReplacementSets();
      const localReplacements: LocalReplacementSets = storageReplacementsParsed
        ? replacementsResponse.data.map((set) => {
            const match = storageReplacementsParsed.find((prev) => prev.set.id === set.id);
            return { checked: match?.checked ?? false, set };
          })
        : replacementsResponse.data.map((replacementSet) => {
            return { checked: false, set: replacementSet };
          });

      localReplacements.sort((a, b) => priorities[a.set.type] - priorities[b.set.type]);
      localStorage.setItem('replacement_sets', JSON.stringify(localReplacements));
      dispatch(updateReplacements({ replacementSets: localReplacements }));
    } catch (error) {
      enqueueSnackbar('Pri pridobivanju skupin zamenjav je prišlo do napake.', { variant: 'error' });
    }
  };

  const fetchBoostSets = async () => {
    const storageBoosts = localStorage.getItem('boost_sets');
    const storageBoostsParsed = storageBoosts ? (JSON.parse(storageBoosts) as LocalBoostSets) : null;

    try {
      const boostsResponse = await getPhrases();

      const localBoosts: LocalBoostSets = storageBoostsParsed
        ? boostsResponse.data.map((set) => {
            const match = storageBoostsParsed.find((prev) => prev.set.id === set.id);
            return { checked: match?.checked ?? false, set };
          })
        : boostsResponse.data.map((boostSet) => {
            return { checked: false, set: boostSet };
          });

      localBoosts.sort((a, b) => priorities[a.set.type] - priorities[b.set.type]);
      localStorage.setItem('boost_sets', JSON.stringify(localBoosts));
      dispatch(updateBoosts({ boostSets: localBoosts }));
    } catch (error) {
      enqueueSnackbar('Pri pridobivanju skupin uteži je prišlo do napake.', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchQuota();
    fetchReplacementSets();
    fetchBoostSets();
  }, [isAuthenticated]);

  useInterval(
    () => {
      fetchQuota();
    },
    15000,
    [isAuthenticated]
  );

  return (
    <ErrorBoundary>
      <Main />
      <Modal visible={visible} closeModal={hideConsumptionModal} title="Omejitev uporabe">
        {(activeQuota && activeQuota.chunk.limitMs && (
          <div className="consumption-modal-content-container">
            {(template === ConsumtionModalTemplate.LIMIT && (
              <>
                <p className="consumption-modal-bold-text">Storitev je (začasno) onemogočena.</p>
                <p className="consumption-modal-regular-text">{`Presegli ste dogovorjeno količino časa uporabe storitve (${transformLimitToReadable(activeQuota.key, activeQuota.chunk.limitMs ? activeQuota.chunk.limitMs : 0)}).`}</p>
                <p className="consumption-modal-regular-text">
                  Za dodatne informacije pišite na{' '}
                  <a className="consumption-modal-email-text" href="mailto:info@vitasis.si">
                    info@vitasis.si
                  </a>
                  .
                </p>
              </>
            )) ||
              (template === ConsumtionModalTemplate.UPLOAD_LIMIT && (
                <>
                  <p className="consumption-modal-bold-text">{`Dosegli ste ${((activeQuota.chunk.processedMs / activeQuota.chunk.limitMs) * 100).toFixed(2)}% vaše ${translateChunkKey(activeQuota.key)} zakupljene količine, oz. ${transformConsumedAndLimitToReadable(activeQuota.chunk.processedMs, activeQuota.chunk.limitMs)}.`}</p>
                  <p className="consumption-modal-regular-text">
                    Z izbrano datoteko bi zakupljene količine presegli, zato storitev ni mogoča.
                  </p>
                  <p className="consumption-modal-regular-text">
                    Izberite krajšo datoteko oz. za dodatne informacije pišite na{' '}
                    <a className="consumption-modal-email-text" href="mailto:info@vitasis.si">
                      info@vitasis.si
                    </a>
                    .
                  </p>
                </>
              )) || (
                <>
                  <p className="consumption-modal-bold-text">{`Dosegli ste ${((activeQuota.chunk.processedMs / activeQuota.chunk.limitMs) * 100).toFixed(2)}% vaše ${translateChunkKey(activeQuota.key)} zakupljene količine, oz. ${transformConsumedAndLimitToReadable(activeQuota.chunk.processedMs, activeQuota.chunk.limitMs)}.`}</p>
                  <p className="consumption-modal-regular-text">
                    Ob presegu dogovorjene količine bo storitev (začasno) onemogočena.
                  </p>
                  <p className="consumption-modal-regular-text">
                    Za dodatne informacije pišite na{' '}
                    <a className="consumption-modal-email-text" href="mailto:info@vitasis.si">
                      info@vitasis.si
                    </a>
                    .
                  </p>
                </>
              )}
          </div>
        )) || <></>}
      </Modal>
      <ReplacementsModal />
      <BoostModal />
    </ErrorBoundary>
  );
};

export default App;
